/**
 * Partner key constants
 */
export const PARTNER_KEYS = {
  CLALIT: "CLALIT",
  MEHUDET: "MEHUDET",
  OVDIM_BARI: "OVDIM_BARI",
  NONE: "NONE",
};

/**
 * Checks if a partner key exists in localStorage
 * @param {string} partnerKey - The partner key to validate
 * @returns {boolean} Returns true if the partner key exists in localStorage
 * @throws {Error} If the partner key is invalid
 */
export function checkPartnerKeyExists(partnerKey) {
  if (!partnerKey || typeof partnerKey !== "string") {
    throw new Error("Valid partner key is required");
  }

  try {
    const storedPartnerKey = localStorage.getItem("partnerKey");

    return storedPartnerKey === partnerKey;
  } catch (error) {
    throw new Error(`Error checking partner key in localStorage: ${error.message}`);
  }
}

/**
 * Returns the ExternalSystem ID for the current partner
 * @returns {number} 1 for Clalit, 2 for Mehudet, 3 for OvdimBari
 */
export function getExternalSystem() {
  if (checkPartnerKeyExists(PARTNER_KEYS.CLALIT)) return 1;
  if (checkPartnerKeyExists(PARTNER_KEYS.MEHUDET)) return 2;
  if (checkPartnerKeyExists(PARTNER_KEYS.OVDIM_BARI)) return 3;
  return 1;
}

/**
 * Returns the request payload fragment identifying the system.
 * - OvdimBari → { MultiExternalSystem: 1 }
 * - Otherwise → { ExternalSystem: getExternalSystem() }
 *
 * Use this for endpoints that already sent ExternalSystem and now must
 * send MultiExternalSystem instead for OvdimBari users.
 * @returns {Object}
 */
export function getExternalSystemParams() {
  if (checkPartnerKeyExists(PARTNER_KEYS.OVDIM_BARI)) {
    return { MultiExternalSystem: 1 };
  }
  return { ExternalSystem: getExternalSystem() };
}

/**
 * Returns { MultiExternalSystem: 1 } for OvdimBari, {} otherwise.
 *
 * Use this for endpoints that previously sent NO system identifier and
 * should only start sending one for OvdimBari (preserving the existing
 * behavior of other partners).
 * @returns {Object}
 */
export function getOvdimBariMultiExternalSystemParams() {
  if (checkPartnerKeyExists(PARTNER_KEYS.OVDIM_BARI)) {
    return { MultiExternalSystem: 1 };
  }
  return {};
}

/**
 * Saves a partner key to localStorage
 * @param {string} partnerKey - The partner key to save
 * @throws {Error} If the partner key is invalid
 */
export function savePartnerKey(partnerKey) {
  if (!partnerKey || typeof partnerKey !== "string") {
    throw new Error("Valid partner key is required");
  }

  try {
    localStorage.setItem("partnerKey", partnerKey);
  } catch (error) {
    throw new Error(`Error saving partner key to localStorage: ${error.message}`);
  }
}

/**
 * Saves the isOrthodox flag to localStorage
 * @param {boolean} isOrthodox - The boolean value to save
 * @throws {Error} If the value is not a boolean
 */
export function saveIsOrthodox(isOrthodox) {
  if (typeof isOrthodox !== "boolean") {
    throw new Error("isOrthodox must be a boolean value");
  }

  try {
    localStorage.setItem("isOrthodox", JSON.stringify(isOrthodox));
  } catch (error) {
    throw new Error(`Error saving isOrthodox to localStorage: ${error.message}`);
  }
}

/**
 * Gets the isOrthodox flag from localStorage
 * @returns {boolean} The stored isOrthodox value, defaults to false if not set
 */
export function getIsOrthodox() {
  try {
    const value = localStorage.getItem("isOrthodox");
    return value ? JSON.parse(value) : false;
  } catch (error) {
    console.error("Error reading isOrthodox from localStorage:", error);
    return false;
  }
}

/**
 * Saves the isSubsidyOver flag to localStorage
 * @param {boolean} isSubsidyOver - The boolean value to save
 * @throws {Error} If the value is not a boolean
 */
export function saveIsSubsidyOver(isSubsidyOver) {
  if (typeof isSubsidyOver !== "boolean") {
    throw new Error("isSubsidyOver must be a boolean value");
  }

  try {
    localStorage.setItem("isSubsidyOver", JSON.stringify(isSubsidyOver));
  } catch (error) {
    throw new Error(`Error saving isSubsidyOver to localStorage: ${error.message}`);
  }
}

/**
 * Gets the isSubsidyOver flag from localStorage
 * @returns {boolean} The stored isSubsidyOver value, defaults to false if not set
 */
export function getIsSubsidyOver() {
  try {
    const value = localStorage.getItem("isSubsidyOver");
    return value ? JSON.parse(value) : false;
  } catch (error) {
    console.error("Error reading isSubsidyOver from localStorage:", error);
    return false;
  }
}

/**
 * Remove the isSubsidyOver flag from localStorage
 * @returns {boolean} The stored isSubsidyOver value, defaults to false if not set
 */
export function removeIsSubsidyOver() {
  localStorage.removeItem("isSubsidyOver");
}

/**
 * Saves the isMinor flag to localStorage
 * @param {boolean} isMinor - The boolean value to save
 * @throws {Error} If the value is not a boolean
 */
export function saveIsMinor(isMinor) {
  if (typeof isMinor !== "boolean") {
    throw new Error("isMinor must be a boolean value");
  }

  try {
    localStorage.setItem("isMinor", JSON.stringify(isMinor));
  } catch (error) {
    throw new Error(`Error saving isMinor to localStorage: ${error.message}`);
  }
}

/**
 * Gets the isMinor flag from localStorage
 * @returns {boolean} The stored isMinor value, defaults to false if not set
 */
export function getIsMinor() {
  try {
    const value = localStorage.getItem("isMinor");
    return value ? JSON.parse(value) : false;
  } catch (error) {
    console.error("Error reading isMinor from localStorage:", error);
    return false;
  }
}
